import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Button, Grid, Card, CardContent, CardActions,
  Chip, CircularProgress, Avatar,
} from "@mui/material";
import { Add, Business, CheckCircle, ArrowForward } from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { listBusinesses } from "../../api/smb.api";

const SmbBusinessList = () => {
  const navigate = useNavigate();
  const { setShowLoginModal } = useAuth();
  const isLoggedIn = localStorage.getItem("isLoggedIn");
  const [businesses, setBusinesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      navigate("/vic", { replace: true });
      return;
    }
    const load = async () => {
      const result = await listBusinesses();
      if (result?.status && result?.data) {
        setBusinesses(result.data);
      }
      setIsLoading(false);
    };
    load();
  }, [isLoggedIn, setShowLoginModal, navigate]);

  if (!isLoggedIn) return null;

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: "#3B82F6" }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#101220", color: "#fff", px: 3, py: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Your Businesses</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/smb/onboarding")}
          sx={{ bgcolor: "#3B82F6", "&:hover": { bgcolor: "#2563EB" }, textTransform: "none" }}
        >
          Add Business
        </Button>
      </Box>

      {businesses.length === 0 ? (
        <Paper sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 3, p: 4, textAlign: "center" }}>
          <Business sx={{ fontSize: 48, color: "#282D51", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>No businesses yet</Typography>
          <Typography variant="body2" sx={{ color: "#9CA3AF", mb: 3 }}>
            Create your first business to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/smb/onboarding")}
            sx={{ bgcolor: "#3B82F6", "&:hover": { bgcolor: "#2563EB" }, textTransform: "none" }}
          >
            Create Business
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {businesses.map((biz) => (
            <Grid item xs={12} sm={6} md={4} key={biz.id}>
              <Card sx={{ bgcolor: "#1B1D2A", border: "1px solid #282D51", borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    <Avatar src={biz.logo_url?.String} sx={{ bgcolor: "#282D51" }}>
                      <Business />
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {biz.business_name?.String || "Untitled"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                        {biz.business_type?.String || "General"}
                      </Typography>
                    </Box>
                    {biz.is_default === 1 && (
                      <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Default" size="small"
                        sx={{ bgcolor: "#10B981", color: "#fff", ml: "auto" }} />
                    )}
                  </Box>
                  <Chip
                    label={biz.onboarding_completed === 1 ? "Setup Complete" : `Step ${biz.onboarding_step}/4`}
                    size="small"
                    sx={{
                      bgcolor: biz.onboarding_completed === 1 ? "rgba(16,185,129,0.2)" : "rgba(59,130,246,0.2)",
                      color: biz.onboarding_completed === 1 ? "#10B981" : "#3B82F6",
                    }}
                  />
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    size="small" endIcon={<ArrowForward />}
                    onClick={() => {
                      if (biz.onboarding_completed === 1) {
                        navigate(`/smb/dashboard/${biz.id}`);
                      } else {
                        navigate(`/smb/onboarding/${biz.id}`);
                      }
                    }}
                    sx={{ color: "#3B82F6", textTransform: "none" }}
                  >
                    {biz.onboarding_completed === 1 ? "Dashboard" : "Continue Setup"}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SmbBusinessList;
